import api, { ApiResponse } from "../api";
import { FinalBOLResult } from "./bolModels";
import {
  ApprovePickupReviewRequest,
  PickupInboxStats,
  PickupRequestDetail,
  PickupRequestEmailByOneXgsPickupNumberResponse,
  PickupStatsAnalytics,
  PickupStatsQuery,
  PickupStatsSummary,
  RejectPickupReviewRequest,
} from "./models";

function getErrorMessage(response: ApiResponse<unknown>): string {
  const err = response.getError?.();
  if (typeof err === "string") return err;
  if (err && typeof err === "object" && "message" in err) return String((err as { message: string }).message);
  return "Request failed";
}

class PickupRequestsService {
  private static instance: PickupRequestsService;

  private constructor() {}

  static getInstance(): PickupRequestsService {
    if (!PickupRequestsService.instance) {
      PickupRequestsService.instance = new PickupRequestsService();
    }
    return PickupRequestsService.instance;
  }

  public getStats = async (): Promise<PickupInboxStats> => {
    const response = await api.get<PickupInboxStats>("/pickups-automation/pickup-requests/requests/stats");
    if (!response.ok()) {
      throw new Error(getErrorMessage(response));
    }
    return response.data;
  };

  public getStatsSummary = async (query?: PickupStatsQuery): Promise<PickupStatsSummary> => {
    const params: Record<string, string> = {};
    if (query?.preset) params.preset = query.preset;
    if (query?.from) params.from = query.from;
    if (query?.to) params.to = query.to;
    if (query?.teamId) params.teamId = query.teamId;

    const response = await api.get<PickupStatsSummary>(
      "/pickups-automation/pickup-requests/requests/stats/summary",
      { params }
    );
    if (!response.ok()) {
      throw new Error(getErrorMessage(response));
    }
    return response.data;
  };

  public getStatsAnalytics = async (query?: PickupStatsQuery): Promise<PickupStatsAnalytics> => {
    const params: Record<string, string> = {};
    if (query?.preset) params.preset = query.preset;
    if (query?.from) params.from = query.from;
    if (query?.to) params.to = query.to;
    if (query?.teamId) params.teamId = query.teamId;

    const response = await api.get<PickupStatsAnalytics>(
      "/pickups-automation/pickup-requests/requests/stats/analytics",
      { params }
    );
    if (!response.ok()) {
      throw new Error(getErrorMessage(response));
    }
    return response.data;
  };

  public getPickupRequest = async (pickupRequestId: number): Promise<PickupRequestDetail> => {
    const response = await api.get<PickupRequestDetail>(
      `/pickups-automation/pickup-requests/requests/${pickupRequestId}`
    );
    if (!response.ok()) {
      throw new Error(getErrorMessage(response));
    }
    return response.data;
  };

  public getEmailByOneXgsPickupNumber = async (
    oneXgsPickupNumber: string
  ): Promise<PickupRequestEmailByOneXgsPickupNumberResponse | null> => {
    const response = await api.get<PickupRequestEmailByOneXgsPickupNumberResponse>(
      `/pickups-automation/pickup-requests/requests/by-one-xgs-pickup-number/${encodeURIComponent(oneXgsPickupNumber)}/email`
    );
    if (response.status === 404) return null;
    if (!response.ok()) {
      throw new Error(getErrorMessage(response));
    }
    return response.data;
  };

  public getBolSnapshot = async (pickupRequestId: number): Promise<FinalBOLResult | null> => {
    const response = await api.get<FinalBOLResult>(
      `/pickups-automation/pickup-requests/requests/${pickupRequestId}/bol/snapshot`
    );
    if (response.status === 404) return null;
    if (!response.ok()) {
      throw new Error(getErrorMessage(response));
    }
    return response.data;
  };

  public getBolCorrection = async (pickupRequestId: number): Promise<FinalBOLResult | null> => {
    const response = await api.get<FinalBOLResult>(
      `/pickups-automation/pickup-requests/requests/${pickupRequestId}/bol/correction`
    );
    if (response.status === 404) return null;
    if (!response.ok()) {
      throw new Error(getErrorMessage(response));
    }
    return response.data;
  };

  public approveReview = async (
    pickupRequestId: number,
    body: ApprovePickupReviewRequest
  ): Promise<void> => {
    const requestBody: ApprovePickupReviewRequest = {
      correctedPayload: body.correctedPayload,
      notes: body.notes,
      teamId: body.teamId,
      pickupDate: body.pickupDate,
      pickupReadyTime: body.pickupReadyTime,
      pickupCloseTime: body.pickupCloseTime,
    };

    const response = await api.post(
      `/pickups-automation/pickup-requests/reviews/${pickupRequestId}/approve`,
      requestBody
    );
    if (!response.ok()) {
      throw new Error(getErrorMessage(response));
    }
  };

  public rejectReview = async (
    pickupRequestId: number,
    body: RejectPickupReviewRequest
  ): Promise<void> => {
    const response = await api.post(
      `/pickups-automation/pickup-requests/reviews/${pickupRequestId}/reject`,
      body
    );
    if (!response.ok()) {
      throw new Error(getErrorMessage(response));
    }
  };
}

export default PickupRequestsService;
